/**
 * Kompakte Modul-Fortschritts-Anzeige für Dashboard-Karten.
 *
 * Anders als der `ModuleStepper` rendert diese Komponente nur eine
 * horizontale 5-Pillen-Reihe — kein Link-Block, kein Sidebar-Layout.
 * Optional eine Zusammenfassung „X von 5 Modulen abgeschlossen" darunter.
 *
 * Lädt die Modul-States für genau einen Workshop und teilt sich dieselben
 * TanStack-Query-Keys mit dem Stepper / den Modul-Pages (geteilter Cache,
 * automatisches Update nach Mutationen).
 */
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

import { MODULE_ORDER, type ModuleProgress } from "./moduleStatus";
import { useWorkshopProgress } from "./useWorkshopProgress";

interface WorkshopProgressProps {
  workshopId: string;
  /** Zeigt unter der Pillen-Reihe „X von 5 Modulen abgeschlossen". */
  showSummary?: boolean;
  className?: string;
}

export function WorkshopProgress({
  workshopId,
  showSummary = true,
  className,
}: WorkshopProgressProps) {
  const { t } = useTranslation();
  const data = useWorkshopProgress(workshopId);
  const completed = MODULE_ORDER.filter(
    (k) => data.progress[k].status === "completed",
  ).length;

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-center gap-1.5" role="list">
        {MODULE_ORDER.map((key, index) => (
          <Pill
            key={key}
            index={index + 1}
            progress={data.progress[key]}
            label={t(`workshops.modules.${key}`)}
          />
        ))}
      </div>
      {showSummary && (
        <p className="text-xs text-muted-foreground">
          {completed === 5
            ? t("workshop_progress.all_done")
            : t("workshop_progress.summary", {
                completed,
                total: MODULE_ORDER.length,
              })}
        </p>
      )}
    </div>
  );
}

interface PillProps {
  index: number;
  progress: ModuleProgress;
  label: string;
}

function Pill({ index, progress, label }: PillProps) {
  if (progress.status === "completed") {
    return (
      <span
        role="listitem"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground"
        title={`${label} — ${progress.status}`}
        aria-label={`${label}: abgeschlossen`}
      >
        <Check className="h-3.5 w-3.5" strokeWidth={3} />
      </span>
    );
  }
  if (progress.status === "in_progress") {
    return (
      <span
        role="listitem"
        className="relative flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-primary text-xs font-semibold tabular-nums text-primary"
        title={`${label} — ${progress.status}`}
        aria-label={`${label}: in Arbeit`}
      >
        <span aria-hidden className="absolute inset-0">
          <span className="block h-full w-1/2 bg-primary/15" />
        </span>
        <span className="relative">{index}</span>
      </span>
    );
  }
  return (
    <span
      role="listitem"
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-muted-foreground/30 text-xs font-medium tabular-nums text-muted-foreground"
      title={`${label} — pending`}
      aria-label={`${label}: offen`}
    >
      {index}
    </span>
  );
}

